'use client'

import s from './HomeComponent.module.scss'
import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import LazyImage from '@/components/Common/LazyImage'
import { sanitizeFilename } from '@/utils/sanitizeFilename'
import { PortableText } from 'next-sanity'
import { portableBlockComponents } from '@/utils/portableText'

type ImageItem = {
  categoryReference?: {
    title: string
  }
  image: {
    imageUrl: string
    filename?: string
    metadata?: {
      dimensions?: {
        width: number
        height: number
      }
    }
  }
}

type StudioBlock = {
  title?: string
  contentLeft?: any[]
  contentRight?: any[]
}

type Props = { data: any; studioBlock?: StudioBlock }

const GRID_ROWS = 3
const GRID_COLS = 5
const GRID_SIZE = GRID_ROWS * GRID_COLS

export default function HomeComponent({ data, studioBlock }: Props) {

  // 1) Normalizamos la lista de items
  const items: ImageItem[] = useMemo(() => {
    if (Array.isArray(data)) return data as ImageItem[]
    if (Array.isArray(data?.items)) return data.items as ImageItem[]
    return []
  }, [data])

  // 2) Agrupar por categoría
  const { categories, groupedByCategory, allImages } = useMemo(() => {
    const grouped: Record<string, ImageItem[]> = {}
    const all: ImageItem[] = []

    items.forEach((item) => {
      if (!item?.image?.imageUrl) return
      const cat = item.categoryReference?.title || 'Uncategorized'
      if (!grouped[cat]) grouped[cat] = []
      grouped[cat].push(item)
      all.push(item)
    })

    const categoryList = Object.keys(grouped)

    return {
      categories: categoryList,
      groupedByCategory: grouped,
      allImages: all,
    }
  }, [items])

  // Helper: construir un grid de 15 imágenes a partir de una lista
  const buildGrid = (source: ImageItem[]): ImageItem[] => {
    if (!source.length) return []
    // barajamos el array de esa categoría
    const shuffled = shuffleArray(source)
    const result: ImageItem[] = []
    for (let i = 0; i < GRID_SIZE; i++) {
      result.push(shuffled[i % shuffled.length])
    }
    return result
  }

  const [isOverHeader, setIsOverHeader] = useState(false)
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0)
  const [displayedImages, setDisplayedImages] = useState<ImageItem[]>([])
  const [isShuffling, setIsShuffling] = useState(false)

  // ✨ control del shuffle (play / pause)
  const [shuffleEnabled, setShuffleEnabled] = useState(true)

  // ✨ estado del cursor personalizado
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })

  const shuffleArray = <T,>(arr: T[]): T[] => {
    const copy = [...arr]
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
        ;[copy[i], copy[j]] = [copy[j], copy[i]]
    }
    return copy
  }

  const [allPreloaded, setAllPreloaded] = useState(false)

  // 🔁 Preload de TODAS las imágenes
  useEffect(() => {
    if (!allImages.length) return
    let isCancelled = false
    let loaded = 0
    const total = allImages.length

    const loaders: HTMLImageElement[] = []

    allImages.forEach((item) => {
      const src = item.image?.imageUrl
      if (!src) return

      const img = new window.Image()
      img.src = src

      const handleLoad = () => {
        loaded += 1
        if (!isCancelled && loaded >= total) {
          setAllPreloaded(true)
          setShuffleEnabled(true) // 👈 activamos el shuffle cuando TODO está cargado
        }
      }

      if (img.complete) {
        // ya está en caché
        handleLoad()
      } else {
        img.onload = handleLoad
        img.onerror = handleLoad // cuenta también errores para no quedarnos colgados
      }

      loaders.push(img)
    })

    return () => {
      isCancelled = true
      loaders.forEach((img) => {
        img.onload = null
        img.onerror = null
      })
    }
  }, [allImages])

  // 3) Inicializar grid con la primera categoría
  useEffect(() => {
    if (!categories.length) return
    const firstCat = categories[0]
    const firstImages = groupedByCategory[firstCat] || []
    setDisplayedImages(buildGrid(firstImages))
  }, [categories, groupedByCategory])


  useEffect(() => {
    const headerEl = document.querySelector('[data-site-header]')
    if (!headerEl) return

    const onEnter = () => setIsOverHeader(true)
    const onLeave = () => setIsOverHeader(false)

    headerEl.addEventListener('mouseenter', onEnter)
    headerEl.addEventListener('mouseleave', onLeave)

    return () => {
      headerEl.removeEventListener('mouseenter', onEnter)
      headerEl.removeEventListener('mouseleave', onLeave)
    }
  }, [])

  const runShuffleCycle = useCallback(() => {
    if (!categories.length || !allImages.length) return

    setIsShuffling(true)

    const shuffleDuration = 1000
    const shuffleSteps = 10
    const stepInterval = shuffleDuration / shuffleSteps
    let step = 0

    const shuffleTimer = setInterval(() => {
      step += 1

      setDisplayedImages(() => {
        const result: ImageItem[] = []
        for (let i = 0; i < GRID_SIZE; i++) {
          const randomIndex = Math.floor(Math.random() * allImages.length)
          result.push(allImages[randomIndex])
        }
        return result
      })

      if (step >= shuffleSteps) {
        clearInterval(shuffleTimer)
        setIsShuffling(false)

        setCurrentCategoryIndex((prev) => {
          const nextIndex = (prev + 1) % categories.length
          const nextCat = categories[nextIndex]
          const nextImages = groupedByCategory[nextCat] || []
          setDisplayedImages(buildGrid(nextImages))
          return nextIndex
        })
      }
    }, stepInterval)
  }, [allImages, categories, groupedByCategory])

  // 4) Intervalo cada 4s → ejecuta un ciclo de shuffle (solo si shuffleEnabled)
  useEffect(() => {
    if (!categories.length || !allImages.length || !shuffleEnabled || !allPreloaded) return

    // 👇 Nada más activarse (al cargar o al darle a Play) ejecuta un shuffle inmediato
    runShuffleCycle()

    const interval = setInterval(() => {
      runShuffleCycle()
    }, 5000)

    return () => clearInterval(interval)
  }, [allImages, categories, shuffleEnabled, allPreloaded, runShuffleCycle])


  // ✨ mover el cursor personalizado
  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      setCursorPos({ x: e.clientX, y: e.clientY })
    }
    window.addEventListener('mousemove', handleMove)
    return () => window.removeEventListener('mousemove', handleMove)
  }, [])

  const toggleShuffle = () => {
    setShuffleEnabled((prev) => !prev)
  }

  const [hasMoved, setHasMoved] = useState(false)

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      setHasMoved(true)
      setCursorPos({ x: e.clientX, y: e.clientY })
    }
    window.addEventListener('mousemove', handleMove, { passive: true })
    return () => window.removeEventListener('mousemove', handleMove as any)
  }, [])

  const hasStudio = Boolean(
    studioBlock?.title || studioBlock?.contentLeft?.length || studioBlock?.contentRight?.length
  )

  const [scrolledPastGrid, setScrolledPastGrid] = useState(false)

  useEffect(() => {
    const onScroll = () => {
      setScrolledPastGrid(window.scrollY > window.innerHeight * 0.3)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className={`${s.home} ${hasStudio ? s.hasStudio : ''}`}>
      <div className={s.gridWrapper}>
        <div
          className={s.grid}
        >
          {displayedImages.map((item, idx) => {
            const img = item.image
            if (!img?.imageUrl) return null

            const width = img.metadata?.dimensions?.width || 480
            const height = img.metadata?.dimensions?.height || 480

            return (
              <div
                key={`${img.imageUrl}-${idx}`}
                className={s.cell}
              >
                <img
                  src={img.imageUrl}
                  alt={sanitizeFilename(img.filename || 'Altherr Désile Park Image')}
                  width={width}
                  height={height}
                  loading="eager"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </div>
            )
          })}
        </div>
      </div>

      {hasStudio && (
        <section
          className={s.studio}
          aria-labelledby="studio-block-heading"
        >
          <div className={s.studioInner}>
            {studioBlock?.title && (
              <h2 id="studio-block-heading" className={s.studioTitle}>
                {studioBlock.title}
              </h2>
            )}
            <div className={s.studioContent}>
              <div className={s.studioColumn}>
                {studioBlock?.contentLeft?.map((textRow: any) => (
                  <PortableText
                    key={textRow._key}
                    value={[textRow]}
                    components={portableBlockComponents()}
                  />
                ))}
              </div>
              <div className={s.studioColumn}>
                {studioBlock?.contentRight?.map((textRow: any) => (
                  <PortableText
                    key={textRow._key}
                    value={[textRow]}
                    components={portableBlockComponents()}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Cursor personalizado PLAY / PAUSE */}
      <div
        className={`${s.cursor} ${
          !allPreloaded ? s.loading : shuffleEnabled ? s.pause : s.play
        } ${isOverHeader || scrolledPastGrid ? s.cursorHidden : ''}`}
        style={{
          transform: hasMoved
            ? `translate(${cursorPos.x}px, ${cursorPos.y}px) translate(-50%, -50%)`
            : `translate(60vw, 57vh) translate(-50%, -50%)`,
          pointerEvents: isOverHeader || scrolledPastGrid ? 'none' : 'auto',
        }}
        onClick={() => {
          if (allPreloaded && !isOverHeader && !scrolledPastGrid) toggleShuffle()
        }}
      >
        {!allPreloaded ? 'Loading' : shuffleEnabled ? 'Pause' : 'Play'}
      </div>
    </div>
  )
}